import React, { useEffect, useState } from 'react';
import {
  Box, Grid, TextField, Button, Alert, Paper, Typography, Divider, List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import { apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { useLocale } from '../../contexts/LocaleContext';
import {
  superAdminSettingsSections,
  superAdminSettingsLinks,
  type SuperAdminSettingsSectionId,
} from '../../config/superAdminMenu';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { RegionalSettingsPanel } from '../../components/settings/RegionalSettingsPanel';
import { normalizeCountryCode } from '../../i18n/localeConfig';

const getIcon = (name: string) => {
  const Icon = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name] || MuiIcons.Settings;
  return <Icon sx={{ fontSize: 20 }} />;
};

export const PlatformSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    applyLocale, t,
  } = useLocale();
  const [section, setSection] = useState<SuperAdminSettingsSectionId>('regional');
  const [form, setForm] = useState({
    defaultLanguage: 'en',
    defaultCurrency: 'INR',
    defaultTimezone: 'Asia/Kolkata',
    defaultCountry: 'IN',
    dateFormat: 'DD/MM/YYYY',
    fileUploadLimitMB: 10,
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await superAdminAPI.getPlatformSettings();
        if (cancelled) return;
        setForm({
          defaultLanguage: r.data.defaultLanguage || 'en',
          defaultCurrency: r.data.defaultCurrency || 'INR',
          defaultTimezone: r.data.defaultTimezone || 'Asia/Kolkata',
          defaultCountry: normalizeCountryCode(r.data.defaultCountry || 'IN'),
          dateFormat: r.data.dateFormat || 'DD/MM/YYYY',
          fileUploadLimitMB: r.data.fileUploadLimitMB || 10,
        });
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, 'Failed to load settings'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        defaultCountry: normalizeCountryCode(form.defaultCountry),
      };
      await superAdminAPI.updatePlatformSettings(payload);
      applyLocale({
        language: payload.defaultLanguage,
        currency: payload.defaultCurrency,
        timezone: payload.defaultTimezone,
        dateFormat: payload.dateFormat,
        country: payload.defaultCountry,
      });
      setMessage('Platform regional settings saved. Defaults apply to new tenants and this admin session.');
      setError('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    }
  };

  return (
    <Box>
      <PageHeader
        title="Platform Settings"
        subtitle={getPageSubtitle('platform_settings')}
        icon={<SettingsIcon />}

        action={
          <Button variant="contained" onClick={handleSave} sx={catchyPrimaryButtonSx}>
            Save Changes
          </Button>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Company logo, sidebar, and table colors are set by each tenant in their own Settings
        (log in as that company&apos;s admin). This page is platform-wide defaults only.
      </Alert>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Settings sidebar */}
        <Paper
          sx={{
            width: 260,
            flexShrink: 0,
            borderRadius: `${RADIUS}px`,
            border: `1px solid ${brandColors.border}`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: `1px solid ${brandColors.border}` }}>
            <Typography fontSize={11} fontWeight={700} color={brandColors.textMuted} letterSpacing={0.6}>
              SETTINGS
            </Typography>
          </Box>
          <List dense disablePadding sx={{ py: 0.5 }}>
            {superAdminSettingsSections.map((s) => (
              <ListItemButton
                key={s.id}
                selected={section === s.id}
                onClick={() => setSection(s.id)}
                sx={{
                  py: 1.25,
                  mx: 0.75,
                  my: 0.25,
                  borderRadius: `${RADIUS}px`,
                  '&.Mui-selected': {
                    bgcolor: brandCss.green50,
                    borderLeft: `3px solid ${brandCss.primary}`,
                    '&:hover': { bgcolor: brandCss.green100 },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: section === s.id ? brandCss.primaryDark : brandColors.textMuted }}>
                  {getIcon(s.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={s.label}
                  secondary={s.description}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: section === s.id ? 700 : 500 }}
                  secondaryTypographyProps={{ fontSize: 11, lineHeight: 1.3 }}
                />
              </ListItemButton>
            ))}
          </List>

          <Divider />
          <Box sx={{ px: 2, py: 1.25, bgcolor: '#f8fafc' }}>
            <Typography fontSize={11} fontWeight={700} color={brandColors.textMuted} letterSpacing={0.6}>
              RELATED
            </Typography>
          </Box>
          <List dense disablePadding sx={{ py: 0.5, pb: 1 }}>
            {superAdminSettingsLinks.map((link) => (
              <ListItemButton
                key={link.path}
                onClick={() => navigate(link.path)}
                sx={{ py: 1, mx: 0.75, borderRadius: `${RADIUS}px` }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: brandColors.textMuted }}>
                  {getIcon(link.icon)}
                </ListItemIcon>
                <ListItemText primary={link.label} primaryTypographyProps={{ fontSize: 13 }} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Settings content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {section === 'regional' && (
            <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
              <RegionalSettingsPanel
                variant="platform"
                values={{
                  language: form.defaultLanguage,
                  currency: form.defaultCurrency,
                  timezone: form.defaultTimezone,
                  dateFormat: form.dateFormat,
                  country: form.defaultCountry,
                }}
                onChange={(patch) => setForm((f) => ({
                  ...f,
                  ...(patch.language !== undefined && { defaultLanguage: patch.language }),
                  ...(patch.currency !== undefined && { defaultCurrency: patch.currency }),
                  ...(patch.timezone !== undefined && { defaultTimezone: patch.timezone }),
                  ...(patch.dateFormat !== undefined && { dateFormat: patch.dateFormat }),
                  ...(patch.country !== undefined && { defaultCountry: patch.country }),
                }))}
                labels={{
                  title: t('regionalSettings'),
                  language: t('language'),
                  currency: t('currency'),
                  timezone: t('timezone'),
                  dateFormat: t('dateFormat'),
                  country: t('country'),
                }}
              />
            </Paper>
          )}

          {section === 'system' && (
            <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
              <Typography variant="h6" fontWeight={700} fontSize={16} mb={0.5}>
                System Limits
              </Typography>
              <Typography fontSize={13} color="text.secondary" mb={2.5}>
                Platform-wide limits applied to all tenants unless overridden by plan quotas.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="File Upload Limit (MB)"
                    type="number"
                    fullWidth
                    size="small"
                    sx={catchyTextFieldSx}
                    value={form.fileUploadLimitMB}
                    onChange={(e) => setForm({ ...form, fileUploadLimitMB: Number(e.target.value) })}
                    helperText="Maximum size per file upload across the platform"
                  />
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};
